import { env } from "@/lib/env"
import { useActiveOrgStore } from "@/features/auth/org.store"
import { resolveApiUrl } from "@/lib/api-url"
import { newRequestId } from "@/lib/requestIds"
import { gateFromBody, gateFromResponse, isPlanCheckUnavailable, notifyPlanCheckUnavailable, parseUploadRefusal } from "@/lib/quotaGate"

export interface ApiErrorBody {
  code: string
  message: string
  severity: string
  category?: string
  retryable?: boolean
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly body: ApiErrorBody,
    public readonly status: number,
    public readonly requestId?: string,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

function isApiErrorBody(obj: unknown): obj is ApiErrorBody {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof (obj as ApiErrorBody).code === "string" &&
    typeof (obj as ApiErrorBody).message === "string" &&
    typeof (obj as ApiErrorBody).severity === "string"
  )
}

export interface TokenOptions {
  skipCache?: boolean
}
type TokenGetter = (opts?: TokenOptions) => Promise<string | null>

let _getToken: TokenGetter = async () => null
let _lastToken: string | null = null

export function configureTokenGetter(fn: TokenGetter): void {
  _getToken = fn
}

/** @deprecated Use configureTokenGetter. Kept for legacy callers and tests. */
export function configureApiClient(provider: () => string | null): void {
  _lastToken = provider()
  _getToken = async () => {
    _lastToken = provider()
    return _lastToken
  }
}

const REQUEST_ID = "X-Request-Id"

function responseRequestId(res: Response): string | undefined {
  return res.headers?.get?.(REQUEST_ID) || undefined
}

function buildHeaders(token: string | null, requestId?: string): Record<string, string> {
  const headers: Record<string, string> = { [REQUEST_ID]: requestId ?? newRequestId() }
  if (token) headers.Authorization = `Bearer ${token}`
  const org = useActiveOrgStore.getState().activeOrgId
  if (org) headers["X-Organization-Id"] = org
  return headers
}

/** Auth headers for a request: Clerk session JWT plus the active organization. */
export async function getAuthHeaders(opts?: TokenOptions): Promise<Record<string, string>> {
  const token = await _getToken(opts)
  _lastToken = token
  return buildHeaders(token)
}

/**
 * @deprecated Sync legacy version for features not yet moved to getAuthHeaders;
 * it reuses the last token getAuthHeaders obtained.
 */
export function authHeaders(): Record<string, string> {
  return buildHeaders(_lastToken)
}

/**
 * fetch() for streaming routes: adds auth headers (fresh on every call, so
 * reconnects pick up a current token) and, on a 401, retries once with a
 * token fetched with skipCache.
 */
export async function fetchWithAuth(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const callerHeaders: Record<string, string> = {}
  let callerRid: string | undefined
  const take = (k: string, v: string) => {
    const lk = k.toLowerCase()
    if (lk === "x-request-id") callerRid = v
    else if (lk === "authorization" || lk === "x-organization-id") return
    else callerHeaders[k] = v
  }
  const raw = init.headers
  if (raw instanceof Headers) raw.forEach((v, k) => take(k, v))
  else if (Array.isArray(raw)) raw.forEach(([k, v]) => take(k, v))
  else if (raw) Object.entries(raw).forEach(([k, v]) => take(k, v))
  const send = (auth: Record<string, string>) =>
    fetch(input, { ...init, headers: { ...callerHeaders, ...auth } })
  const first = await getAuthHeaders()
  if (callerRid) first[REQUEST_ID] = callerRid
  const res = await send(first)
  if (res.status !== 401) {
    await gateFromResponse(res)
    return res
  }
  const auth = await getAuthHeaders({ skipCache: true }).catch(() => null)
  if (!auth?.Authorization) return res
  const retried = await send({ ...auth, [REQUEST_ID]: first[REQUEST_ID] })
  await gateFromResponse(retried)
  return retried
}

/** Fetch with auth headers; on a 401 refresh the token once (skipCache) and retry once. */
async function authedFetch(
  input: RequestInfo | URL,
  makeInit: (auth: Record<string, string>) => RequestInit,
): Promise<Response> {
  const first = await getAuthHeaders()
  const res = await fetchWithRetry(input, makeInit(first))
  if (res.status !== 401) return res
  const fresh = await _getToken({ skipCache: true }).catch(() => null)
  if (!fresh) return res
  _lastToken = fresh
  return fetchWithRetry(input, makeInit(buildHeaders(fresh, first[REQUEST_ID])))
}

function isSessionAuthFailure(detail: string | undefined): boolean {
  if (!detail) return false
  return (
    detail === "Not authenticated" ||
    detail.startsWith("Invalid access token:") ||
    detail.startsWith("API key") ||
    detail.startsWith("Invalid API key")
  )
}

async function handleResponseError(res: Response, body: unknown): Promise<never> {
  if (res.status === 401) {
    const detail = typeof body === "object" && body !== null && "detail" in body
      ? String((body as { detail: unknown }).detail)
      : undefined
    if (isSessionAuthFailure(detail)) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"))
      throw Object.assign(new Error("Unauthorized"), { status: 401 })
    }
    if (isApiErrorBody(body)) {
      throw new ApiError(body.message, body, res.status, responseRequestId(res))
    }
    const code = typeof body === "object" && body !== null && typeof (body as { code?: unknown }).code === "string"
      ? (body as { code: string }).code
      : undefined
    throw Object.assign(new Error(detail ?? "HTTP 401"), { status: 401, requestId: responseRequestId(res), code })
  }

  // A 503 plan check: a calm retry notice, never the paywall. The caller gets the error too.
  if (isPlanCheckUnavailable(res.status, body)) {
    const message = String((body as { message?: unknown }).message ?? "")
    notifyPlanCheckUnavailable(message || null)
    throw Object.assign(new Error(message || "plan_check_unavailable"), { status: 503, code: "plan_check_unavailable", message, requestId: responseRequestId(res) })
  }
  // A 413 (too large) or 415 (unsupported) upload: the server's own sentence, shown where the upload was.
  const refusal = parseUploadRefusal(res.status, body)
  if (refusal) {
    throw Object.assign(new Error(refusal.message), { status: refusal.status, code: refusal.code, message: refusal.message, limitBytes: refusal.limitBytes, feature: refusal.feature, requestId: responseRequestId(res) })
  }

  if (res.status === 402 && gateFromBody(body)) {
    const code = (body as { code?: string }).code
    throw Object.assign(new Error(code ?? "quota"), { status: 402, requestId: responseRequestId(res), code, quota: body })
  }
  if (isApiErrorBody(body)) {
    throw new ApiError(body.message, body, res.status, responseRequestId(res))
  }
  const detail = typeof body === "object" && body !== null && "detail" in body
    ? String((body as { detail: unknown }).detail)
    : `HTTP ${res.status}`
  const error = new Error(detail) as Error & { status: number; requestId?: string }
  error.status = res.status
  error.requestId = responseRequestId(res)
  throw error
}

async function handleResponse<T>(res: Response): Promise<T> {
  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = { detail: res.statusText }
  }

  if (!res.ok) {
    await handleResponseError(res, body)
  }

  if (res.status === 204) return undefined as unknown as T
  return body as T
}

async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  maxRetries = 3,
): Promise<Response> {
  let attempt = 0
  while (true) {
    const res = await fetch(input, init)
    if (res.status === 429 && attempt < maxRetries) {
      attempt++
      const retryAfterHeader = res.headers.get("Retry-After")
      let delayMs = 300 * Math.pow(2, attempt) + Math.random() * 200
      if (retryAfterHeader) {
        const parsed = parseFloat(retryAfterHeader)
        if (!isNaN(parsed) && parsed > 0) {
          delayMs = Math.max(parsed * 1000, delayMs)
        }
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs))
      continue
    }
    return res
  }
}

export const apiClient = {
  async get<T>(path: string, query?: Record<string, string>, signal?: AbortSignal): Promise<T> {
    const url = new URL(resolveApiUrl(env.VITE_API_URL, path), window.location.origin)
    if (query) {
      Object.entries(query).forEach(([k, v]) => url.searchParams.set(k, v))
    }
    const res = await authedFetch(url.toString(), (auth) => ({
      headers: { ...auth },
      credentials: "include",
      signal,
    }))
    return handleResponse<T>(res)
  },

  async post<T>(path: string, body?: unknown, options?: { headers?: Record<string, string> }): Promise<T> {
    const res = await authedFetch(resolveApiUrl(env.VITE_API_URL, path), (auth) => ({
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth, ...options?.headers },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "include",
    }))
    return handleResponse<T>(res)
  },

  async put<T>(path: string, body?: unknown): Promise<T> {
    const res = await authedFetch(resolveApiUrl(env.VITE_API_URL, path), (auth) => ({
      method: "PUT",
      headers: { "Content-Type": "application/json", ...auth },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "include",
    }))
    return handleResponse<T>(res)
  },

  async patch<T>(path: string, body?: unknown): Promise<T> {
    const res = await authedFetch(resolveApiUrl(env.VITE_API_URL, path), (auth) => ({
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...auth },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "include",
    }))
    return handleResponse<T>(res)
  },

  async delete<T>(path: string): Promise<T> {
    const res = await authedFetch(resolveApiUrl(env.VITE_API_URL, path), (auth) => ({
      method: "DELETE",
      headers: { ...auth },
      credentials: "include",
    }))
    return handleResponse<T>(res)
  },

  async upload<T>(path: string, formData: FormData): Promise<T> {
    const res = await authedFetch(resolveApiUrl(env.VITE_API_URL, path), (auth) => ({
      method: "POST",
      headers: { ...auth }, // No Content-Type — browser sets multipart boundary
      body: formData,
      credentials: "include",
    }))
    return handleResponse<T>(res)
  },

  async postForBlob(path: string, body?: unknown): Promise<Blob> {
    const res = await authedFetch(resolveApiUrl(env.VITE_API_URL, path), (auth) => ({
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "include",
    }))

    if (res.ok) return res.blob()

    let errorBody: unknown
    try {
      errorBody = await res.json()
    } catch {
      errorBody = { detail: res.statusText }
    }
    return handleResponseError(res, errorBody)
  },
}
